import React from 'react';
import {
  Card, CardContent, Box, Typography, TextField, Switch, Chip,
  IconButton, Autocomplete, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

function extractTimeUnit(featureKey) {
  const match = featureKey.match(/per_(minute|hour|day|week|month|year)s?$/);
  if (match) return `per ${match[1]}`;
  if (featureKey.match(/active_|^limits\./)) return '(count-based)';
  return '';
}

function NumericRestriction({ value, constraints, timeUnit, onChange, isDraft }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField
        type="number"
        value={value ?? 0}
        onChange={(e) => onChange(parseInt(e.target.value) || 0)}
        disabled={!isDraft}
        size="small"
        sx={{ width: 80 }}
        inputProps={{ min: constraints?.min ?? 0 }}
      />
      {timeUnit && (
        <Typography variant="caption" color="text.secondary">{timeUnit}</Typography>
      )}
    </Box>
  );
}

function BooleanRestriction({ value, isInverted, displayName, onChange, isDraft }) {
  const label = isInverted
    ? (value ? 'Restricted' : 'Unrestricted')
    : (value ? 'Enabled' : 'Disabled');

  return (
    <FormControlLabel
      control={
        <Switch
          checked={isInverted ? value === true : value === false}
          onChange={(e) => {
            const newVal = isInverted ? e.target.checked : !e.target.checked;
            onChange(newVal);
          }}
          disabled={!isDraft}
          size="small"
        />
      }
      label={<Typography variant="caption">{label}</Typography>}
    />
  );
}

function OptionSetRestriction({ value, allOptions, onChange, isDraft }) {
  const currentValue = Array.isArray(value) ? value : [];

  return (
    <Autocomplete
      multiple
      size="small"
      options={allOptions}
      value={currentValue}
      onChange={(e, newValue) => onChange(newValue)}
      disabled={!isDraft}
      renderInput={(params) => (
        <TextField {...params} size="small" placeholder="Available options..." />
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip label={option} size="small" {...getTagProps({ index })} key={option} />
        ))
      }
      sx={{ minWidth: 200 }}
      disableCloseOnSelect
    />
  );
}

export default function RestrictionCard({ featureKey, value, catalogEntry, isDraft, onChange, onRemove }) {
  const constraints = catalogEntry?.constraints;
  const isInverted = constraints?.inverted === true;
  const timeUnit = extractTimeUnit(featureKey);

  return (
    <Card variant="outlined" sx={{ mb: 1, backgroundColor: 'background.paper' }}>
      <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isDraft && (
            <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'grab' }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight="bold" noWrap>
              {catalogEntry?.display_name || featureKey}
            </Typography>
            {catalogEntry?.feature_type === 'numeric' && (
              <NumericRestriction
                value={value}
                constraints={constraints}
                timeUnit={timeUnit}
                onChange={onChange}
                isDraft={isDraft}
              />
            )}
            {catalogEntry?.feature_type === 'boolean' && (
              <BooleanRestriction
                value={value}
                isInverted={isInverted}
                displayName={catalogEntry.display_name}
                onChange={onChange}
                isDraft={isDraft}
              />
            )}
            {catalogEntry?.feature_type === 'option_set' && (
              <OptionSetRestriction
                value={value}
                allOptions={constraints?.options || []}
                onChange={onChange}
                isDraft={isDraft}
              />
            )}
          </Box>
          {isDraft && (
            <IconButton size="small" onClick={onRemove} sx={{ color: 'text.secondary' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
